import { Button, Stack, useTheme } from "@mui/material";
import { CircleMinus } from "lucide-react";
import type { FC } from "react";
import { type UseFormReturn, useWatch } from "react-hook-form";
import type { ClientPortfolioInsuredPersonRiskEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type {
	InsuredPersonInput,
	InsuredPersonOutput,
} from "web_myplann/Portfolio/Forms/LifeInsuranceForm/InsuredPersons/insuredPersonSchema";
import type { useInsuranceRiskOptions } from "web_myplann/Portfolio/Forms/LifeInsuranceForm/InsuredPersons/useInsuranceRiskOptions";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";

type Props = {
	form: UseFormReturn<InsuredPersonInput, unknown, InsuredPersonOutput>;
	index: number;
	riskOptions: ReturnType<typeof useInsuranceRiskOptions>;
	onRemove: () => void;
};

const PageResources = Resources.Portfolio.AddLifeInsurance;

export const InsuredRiskFields: FC<Props> = ({
	form,
	index,
	riskOptions,
	onRemove,
}) => {
	const { t } = useResource();
	const theme = useTheme();

	const risk = useWatch({ control: form.control, name: `risks.${index}.risk` });

	const resetDisallowedVariant = (
		nextRisk: ClientPortfolioInsuredPersonRiskEnum,
	) => {
		const variant = form.getValues(`risks.${index}.variant`);
		const isAllowed = riskOptions
			.getVariantOptions(nextRisk)
			.some((option) => option.value === variant);

		if (!isAllowed) {
			form.setValue(`risks.${index}.variant`, null, { shouldDirty: true });
		}
	};

	return (
		<Stack
			sx={{ borderBottom: theme.colors.border.solid(theme.palette.grey[100]) }}
		>
			<FlatRowFormSelect
				control={form.control}
				name={`risks.${index}.risk`}
				label={t(PageResources.InsuredPersons.risk)}
				placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
				options={riskOptions.riskOptions}
				onValueChange={resetDisallowedVariant}
			/>
			<FlatRowFormInput
				control={form.control}
				name={`risks.${index}.value`}
				type="float"
				label={t(PageResources.InsuredPersons.value)}
				placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				suffix="Kč"
			/>
			<FlatRowFormInput
				control={form.control}
				name={`risks.${index}.toAge`}
				type="number"
				label={t(PageResources.InsuredPersons.toAge)}
				placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				suffix={t(PageResources.Units.years)}
			/>
			<FlatRowFormSelect
				control={form.control}
				name={`risks.${index}.variant`}
				label={t(PageResources.InsuredPersons.variant)}
				placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
				options={riskOptions.getVariantOptions(risk)}
				isLast
			/>
			<Stack alignItems="center" py={theme.spacingTokens.sm}>
				<Button
					variant="textLink"
					color="error"
					startIcon={<CircleMinus size={theme.iconSize.sm} />}
					onClick={onRemove}
				>
					{t(PageResources.InsuredPersons.removeRisk)}
				</Button>
			</Stack>
		</Stack>
	);
};
